/**
 * see test cards: https://docs.stripe.com/testing#cards
 */

import preact, { h, Fragment } from 'preact'; /** @jsx h */
import { useState, useEffect } from 'preact/hooks';
import { saveAs } from 'file-saver';
import { clsx } from 'clsx';
import { ROUTES } from '@pyqml/shared';
import { Session } from '@pyqml/model';
import { Layout } from '../components/Layout';
import { Subheader } from '../components/Subheader';
import { useAuth } from '../app/AuthProvider';


import BlogCover from '../assets/blog-cover.webp';
import TeaserVol1 from '../assets/teaser-vol1.webp';
import TeaserVol2 from '../assets/teaser-vol2.webp';

import TeaserVol1Ed2 from '../assets/feynman.png';
import TeaserVol2Ed2 from '../assets/algorithm.webp';
import TeaserVol3Ed2 from '../assets/eniac.png';
import TeaserVol4Ed2 from '../assets/future.png';



const Feature = ({text, bold = false}) => (
    <li>
        {/*<span className="iconsmall" style={{marginRight: "0.5em"}}><i className="fas fa-check"></i></span>*/}
        {
            bold ? <b>{text}</b> : text
        }
    </li>
);

const Teaser = ({img}) => <div style={{
    backgroundImage: `url(${img})`,
    height: "280px",
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat"
}}></div> ; 

const PaywalledBook = ({level, icon, children}) => (
    <div style={{
        display: "flex",
        alignItems: "center",
        paddingBottom: "3rem"
    }}>
        <span
            className={clsx("iconbox iconsmall rounded-circle text-white border-0", "bg-" + level)}
            style={{ margin: "0.5em"}}
        >
            <i className={icon} style={{ width: "40px"}}></i>
        </span>
        <span>{ children }</span>
        
    </div>
);

export const Bookshelf = (props) => {
    
    const { subscriptionLevel, subscriptionDuration, authPromise } = useAuth();
    
    const download = (resourceId: string) => {
        authPromise?.({
            method: "post",
            endpoint: ROUTES.CLOUDAPI_PRIVATE_GET_DOWNLOAD_LINK,
            queryData: {
                resourceId: resourceId
            }
        }).then(response => {
            if (response?.data?.fileUrl && response?.data?.fileName) {
                //var blob = new Blob([response.data], { type: response?.header?.["content-type"]});
                saveAs(response?.data?.fileUrl, response?.data?.fileName);
            } else {
                // TODO show notification
                console.log("could not load resource")
            }
              
        })

    };

    return <div>

        <div style={{
            backgroundImage: `url(${BlogCover})`,
            backgroundSize: "cover",
            margin: 0,
            color: "white",
            padding: "80px 0",
            textAlign: "center",
        }}>
            <span style={{
                display: "block",
                fontFamily: "ShadowsIntoLight",
                fontSize: "3em",
                margin: 0,
                padding: "8px",
                lineHeight: "1.1",
                fontWeight: "bold",
            }}>Bookshelf</span>
            <span style={{
                fontSize: "1.3em",
                margin: 0,
                opacity: 0.8,
            }}>Hands-On Quantum Machine Learning With Python</span>
    
        </div>
        <Layout.Stacked>
        
            <Layout.Section weight="equal">
                
                <div weight="top">
                    <h2 >First Editions</h2>
                    <p>
                        
                        You're interested in quantum computing and machine learning...<br/>
                        ...But you don't know how to get started? <b>Let me help!</b>
                    </p>
                    <p>
                        Whether you just get started with quantum computing and machine learning or you're
                        already a senior machine learning engineer, <b>Hands-On Quantum Machine Learning With Python</b>
                        is your comprehensive guide to get started with <b>Quantum Machine Learning</b> - the use of
                        quantum computing for the computation of machine learning algorithms.
                    </p>
                    <p>
                        The first editions of Volumes 1 + 2 are available now. These are more than 800 pages of
                        content that will guide you through the basics of quantum computing and machine learning.
                    </p>
                </div>

                <Layout.Column>
                    <Subheader title="Volume 1: Getting Started" tight />
                    <Teaser img={TeaserVol1} />
                    <ul className="features">
                        <Feature text="Basic and advanced machine learning techniques" />
                        <Feature text="Quantum computing introduction" />
                        <Feature text="Parameterized quantum circuits" />
                        <Feature text="Variational hybrid quantum-classical algorithms" />
                        <Feature text="Lots of examples" />
                        
                    </ul>

                    
                    {
                        Session.satisfiesRequiredSubscriptionLevelAndDuration(
                            subscriptionLevel,
                            "student",
                            subscriptionDuration,
                            "year"
                        ) ? <p className="btn-cta">
                                <a href="#" className="btn btn-student" onClick={() => download("ed1-volume1")}  style={{marginBottom: "5rem !important"}}>Download</a>
                            </p>
                        : <PaywalledBook level="student" icon="fab fa-leanpub">
                            This eBook is included in all <b>annual</b> subscriptions.
                        </PaywalledBook>
                    }
                    
                    

                </Layout.Column>
                <Layout.Column>
                    <Subheader title="Volume 2: Combinatorial Optimization" tight />
                    <Teaser img={TeaserVol2} />
                    
                    <ul className="features">
                        <Feature text="The Variational Quantum Eigensolver (VQE)" />
                        <Feature text="Various solution ansatzes" />
                        <Feature text="Running algorithms on real quantum computers" />
                        <Feature text="Quantum error mitigation" />
                        <Feature text="The Quantum Approximate Optimization Algorithm (QAOA)" />
                    </ul>

                    {
                        Session.satisfiesRequiredSubscriptionLevelAndDuration(
                            subscriptionLevel,
                            "practitioner",
                            subscriptionDuration,
                            "year"
                        ) ? <p className="btn-cta">
                                <a href="#" className="btn btn-practitioner" onClick={() => download("ed1-volume2")}  style={{marginBottom: "5rem !important"}}>Download</a>
                            </p>
                        : <PaywalledBook level="practitioner" icon="fas fa-user-astronaut">
                            This eBook is included in the <b>annual practitioner and researcher</b> subscriptions.
                        </PaywalledBook>
                    }

                </Layout.Column>
                
                    
            </Layout.Section>
            
            <Layout.Section weight="equal">
                <hr/>
                <div weight="top">
                    <h2 >Second Editions - Early Access</h2>
                    <p>
                        Since I published the first two volumes of <b>Hands-On Quantum Machine Learning With Python</b>
                        in 2021 and 2022, the field of quantum machine learning has evolved significantly.
                    </p>
                    <ul>
                        <li>
                            IBM released <b>Qiskit 1.0</b> - their Quantum SDK - with major changes in the API.
                            For <b>Hands-On Quantum Machine Learning With Python</b> uses this SDK extensively, many
                            parts of the first editions require updates.
                        </li>
                        <li>
                            IBM also made their <b>100+ qubit</b> quantum computers available to the public. These
                            computers allow us to run quantum algorithms on real quantum hardware that we
                            can't simulate on classical computers anymore.
                        </li>
                        <li>
                            The subfield of error-correction and error-mitigation has seen significant progress
                            and make quantum computers much more reliable. Therefore, we are now entering the phase
                            of <b>quantum-utility</b>. This means that quantum computers
                            have become a viable alternative to classical computers for certain tasks.
                        </li>
                    </ul>
                    
                
                </div>

                <Layout.Column>
                    <Subheader title="Volume 1: Quantum Advantage in Machine Learning" tight  />
                    <Teaser img={TeaserVol1Ed2} />

                    <ul className="features">
                        <Feature text="Basics of Quantum Information" />
                        <Feature text="Quantum Bayesian Networks" />
                        <Feature text="Quantum Support Vector Machines" />
                        <Feature text="Grover's search algorithm" />
                    </ul>

                    <PaywalledBook level="student" icon="fab fa-leanpub">
                        Coming soon...
                    </PaywalledBook>

                    { /*
                        
                        Session.satisfiesRequiredSubscriptionLevelAndDuration(
                            subscriptionLevel,
                            "student",
                            subscriptionDuration,
                            "month"
                        ) ? <p className="btn-cta">
                                <a href="#" className="btn btn-student" onClick={() => download("earlyaccess-volume1")}  style={{marginBottom: "5rem !important"}}>Download</a>
                            </p>
                        : <PaywalledBook level="student" icon="fab fa-leanpub">
                            Early access to this eBook is included in <b>all</b> subscriptions.
                        </PaywalledBook>
                    */ }
                    

                </Layout.Column>
                <Layout.Column>
                    <Subheader title="Volume 2: Real World Applications" tight />
                    <Teaser img={TeaserVol2Ed2} />

                    <ul className="features">
                        <Feature text="Variational Quantum Eigensolver" />
                        <Feature text="Quantum Approximate Optimization Algorithm" />
                        <Feature text="Quantum Error Mitigation" />
                        <Feature text="Running circuits on real devices" />
                        
                    </ul>

                    <PaywalledBook level="practitioner" icon="fas fa-user-astronaut">
                        Coming soon...
                    </PaywalledBook>
                    { /*
                        Session.satisfiesRequiredSubscriptionLevelAndDuration(
                            subscriptionLevel,
                            "practitioner",
                            subscriptionDuration,
                            "month"
                        ) ? <p className="btn-cta">
                                <a href="#" className="btn btn-practitioner" onClick={() => download("earlyaccess-volume2")}  style={{marginBottom: "5rem !important"}}>Download</a>
                            </p>
                        : <PaywalledBook level="practitioner" icon="fas fa-user-astronaut">
                            Early access to this eBook is included in the <b>practitioner and researcher</b> subscriptions.
                        </PaywalledBook>
                    */ }

                </Layout.Column>
                <Layout.Column>
                    <Subheader title="Volume 3: Predicting The Future" tight />
                    <Teaser img={TeaserVol3Ed2} />
                    
                    <ul className="features">
                        <Feature text="Quantum Phase Estimation" />
                        <Feature text="Quantum Fourier Transform" />
                        <Feature text="Shor's algorithm" />
                        <Feature text="Quantum Principal Component Analysis" />
                        
                    </ul>

                    <PaywalledBook level="researcher" icon="fas fa-flask">
                        Coming soon...
                    </PaywalledBook>
                    {/*
                        
                        Session.satisfiesRequiredSubscriptionLevelAndDuration(
                            subscriptionLevel,
                            "researcher",
                            subscriptionDuration,
                            "month"
                        ) ? <p className="btn-cta">
                                <a href="#" className="btn btn-researcher" onClick={() => download("earlyaccess-volume3")}  style={{marginBottom: "5rem !important"}}>Download</a>
                            </p>
                        : <PaywalledBook level="researcher" icon="fas fa-flask">
                            Early access to this eBook is included in the <b>researcher</b> subscription.
                        </PaywalledBook>
                    */}

                </Layout.Column>
                <Layout.Column>
                    <Subheader title="Volume 4: Toward Artificial General Intelligence" tight />
                    <Teaser img={TeaserVol4Ed2} />
                    
                    <ul className="features">
                        <Feature text="Pattern Recognition" />
                        <Feature text="Solution space search" />
                        <Feature text="Automated Reasoning" />
                        <Feature text="One-shot learning" />
                    </ul>

                    <PaywalledBook level="researcher" icon="fas fa-flask">
                        Coming soon...
                    </PaywalledBook>
                    {/*
                        
                        Session.satisfiesRequiredSubscriptionLevelAndDuration(
                            subscriptionLevel,
                            "researcher",
                            subscriptionDuration,
                            "month"
                        ) ? <p className="btn-cta">
                                <a href="#" className="btn btn-researcher" onClick={() => download("earlyaccess-volume4")}  style={{marginBottom: "5rem !important"}}>Download</a>
                            </p>
                        : <PaywalledBook level="researcher" icon="fas fa-flask">
                            Early access to this eBook is included in the <b>researcher</b> subscription.
                        </PaywalledBook>
                    */}
                </Layout.Column>
                
                    
            </Layout.Section>

            
        </Layout.Stacked>
    </div>
            
}
  
